import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./css/inventoryModern.css";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import ProductItem from "./elements/ProductItem";
import ProductEditorModal from "./elements/ProductEditorModal";
import InventoryImportModal from "./elements/InventoryImportModal";
import InventoryToast from "./elements/InventoryToast";
import {
  archiveProduct,
  downloadProductsXLSX,
  searchProducts,
} from "./logic/apiCalls";

function readStoredObject(key) {
  try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {}; }
}

function Inventory({ embedded = false, active = true, searchQuery } = {}) {
  const Heading = embedded ? "h2" : "h1";
  const availableLocations = useMemo(() => readStoredObject("availableLocations"), []);
  const categoryStructure = useMemo(() => readStoredObject("availableCategories"), []);
  const saleUnits = useMemo(() => readStoredObject("availableSaleUnits"), []);
  const locationIds = useMemo(() => Object.keys(availableLocations), [availableLocations]);
  const [selectedLocations, setSelectedLocations] = useState(locationIds);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [query, setQuery] = useState(searchQuery || "");
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 30, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [editor, setEditor] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const fileInputRef = useRef(null);
  const locationRef = useRef(null);

  const loadProducts = useCallback(() => {
    if (selectedLocations.length === 0) {
      setProducts([]);
      setPagination((current) => ({ ...current, total: 0, totalPages: 1 }));
      setLoading(false);
      setError("");
      return () => {};
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = window.setTimeout(() => {
      searchProducts({ query, locations: selectedLocations, page, pageSize: 30, signal: controller.signal })
        .then((result) => {
          setProducts(result.products);
          setPagination(result.pagination);
        })
        .catch((loadError) => {
          if (loadError.name !== "AbortError") setError(loadError.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 280);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [page, query, selectedLocations]);

  useEffect(() => { if (active) return loadProducts(); }, [active, loadProducts, refreshToken]);
  useEffect(() => { if (searchQuery !== undefined) setQuery(searchQuery); }, [searchQuery]);
  useEffect(() => { setPage(1); }, [query, selectedLocations]);

  useEffect(() => {
    const close = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) setLocationsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const refresh = () => setRefreshToken((value) => value + 1);
  const openEditor = (product, tab = "general") => setEditor({ mode: "edit", product, initialTab: tab });
  const openCreate = () => setEditor({ mode: "create", product: null, initialTab: "general" });
  const requestEditorClose = (dirty = false) => {
    if (dirty) setPendingClose(true);
    else setEditor(null);
  };
  const handleSaved = (savedProduct, { keepOpen = true } = {}) => {
    if (savedProduct) {
      setProducts((current) => {
        const exists = current.some((item) => item.id === savedProduct.id);
        return exists ? current.map((item) => item.id === savedProduct.id ? { ...item, ...savedProduct } : item) : [savedProduct, ...current];
      });
      if (editor?.mode === "edit" && keepOpen) setEditor((current) => ({ ...current, product: { ...current.product, ...savedProduct } }));
    }
    setNotice({ type: "success", text: editor?.mode === "create" ? "Producto creado correctamente." : "Producto actualizado." });
    if (editor?.mode === "create" || !keepOpen) setEditor(null);
    refresh();
  };

  const confirmArchive = async () => {
    const target = archiveTarget;
    setArchiveTarget(null);
    try {
      await archiveProduct(target.id, "Archivado desde administración de inventario");
      setProducts((current) => current.filter((item) => item.id !== target.id));
      setNotice({ type: "success", text: `“${target.description || target.code}” fue archivado. Su historial se conserva.` });
    } catch (archiveError) {
      setNotice({ type: "error", text: archiveError.message });
    }
  };

  const exportProducts = async () => {
    setNotice({ type: "info", text: "Preparando archivo XLSX…" });
    try {
      await downloadProductsXLSX(selectedLocations);
      setNotice({ type: "success", text: "Inventario exportado. Los códigos se guardaron como texto." });
    } catch (exportError) {
      setNotice({ type: "error", text: exportError.message });
    }
  };

  const visibleShortages = products.filter((product) => Number(product.stock_shortage_qty) > 0).length;
  const visibleLowStock = products.filter((product) => Number(product.qty) > 0 && Number(product.qty) <= 5).length;
  const visibleValue = products.reduce((sum, product) => sum + Number(product.qty || 0) * Number(product.unit_cost || 0), 0);
  const money = new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 });

  return (
    <main className="inventory-page">
      <header className="inventory-page-header">
        <div><span className="inventory-eyebrow">{embedded ? "Catálogo compartido · Operaciones" : "Kakao · Operaciones"}</span><Heading>{embedded ? "Inventario y operaciones" : "Inventario"}</Heading><p>Controla productos, precios, etiquetas y movimientos con trazabilidad por ubicación.</p></div>
        <div className="inventory-header-actions">
          <button type="button" className="inventory-secondary-button" onClick={exportProducts}><span className="material-symbols-outlined">download</span> Exportar</button>
          <button type="button" className="inventory-secondary-button" onClick={() => fileInputRef.current?.click()}><span className="material-symbols-outlined">upload_file</span> Importar</button>
          <button type="button" className="inventory-primary-button" onClick={openCreate}><span className="material-symbols-outlined">add</span> Agregar producto</button>
          <input ref={fileInputRef} type="file" accept=".xlsx" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) setImportFile(file); event.target.value = ""; }} />
        </div>
      </header>

      <section className="inventory-summary-grid" aria-label="Resumen de los resultados visibles">
        <article><span className="material-symbols-outlined">inventory_2</span><div><small>Productos</small><strong>{pagination.total.toLocaleString("es-CL")}</strong></div></article>
        <article className="is-danger"><span className="material-symbols-outlined">production_quantity_limits</span><div><small>Ventas sin stock en esta página</small><strong>{visibleShortages}</strong></div></article>
        <article className="is-warning"><span className="material-symbols-outlined">warning</span><div><small>Stock bajo en esta página</small><strong>{visibleLowStock}</strong></div></article>
        <article className="is-value"><span className="material-symbols-outlined">account_balance_wallet</span><div><small>Valor visible a costo</small><strong>{money.format(visibleValue)}</strong></div></article>
      </section>

      <section className="inventory-toolbar" aria-label="Buscar y filtrar inventario">
        <label className="inventory-search"><span className="material-symbols-outlined">search</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por producto, marca o código" aria-label="Buscar productos" />{query ? <button type="button" aria-label="Limpiar búsqueda" onClick={() => setQuery("")}><span className="material-symbols-outlined">close</span></button> : null}</label>
        <div className="inventory-location-filter" ref={locationRef}>
          <button type="button" className="inventory-filter-button" aria-haspopup="true" aria-expanded={locationsOpen} onClick={() => setLocationsOpen((open) => !open)}><span className="material-symbols-outlined">location_on</span>{selectedLocations.length === locationIds.length ? "Todas las ubicaciones" : selectedLocations.length ? `${selectedLocations.length} ubicaciones` : "Sin ubicación"}<span className="material-symbols-outlined inventory-ios-arrow" aria-hidden="true">expand_more</span></button>
          {locationsOpen ? <div className="inventory-location-menu"><div><strong>Ubicaciones</strong><button type="button" onClick={() => setSelectedLocations(selectedLocations.length === locationIds.length ? [] : locationIds)}>{selectedLocations.length === locationIds.length ? "Limpiar" : "Seleccionar todas"}</button></div>{Object.entries(availableLocations).map(([id, name]) => <label key={id}><input type="checkbox" checked={selectedLocations.includes(id)} onChange={() => setSelectedLocations((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id])} /><span>{name}</span></label>)}</div> : null}
        </div>
        <button type="button" className="inventory-icon-button" onClick={refresh} aria-label="Actualizar inventario" title="Actualizar"><span className="material-symbols-outlined">refresh</span></button>
      </section>

      {notice ? <InventoryToast type={notice.type} message={notice.text} onClose={() => setNotice(null)} /> : null}

      <section className="inventory-results" aria-label="Productos de inventario">
        <div className="inventory-results-header"><span>Producto</span><span>Categoría</span><span>Stock</span><span>Precio</span><span>Margen</span><span>Acciones</span></div>
        {loading ? <div className="inventory-row-skeletons" aria-label="Cargando productos">{Array.from({ length: 6 }).map((_, index) => <div key={index} />)}</div> : null}
        {!loading && error ? <div className="inventory-empty-state is-error"><span className="material-symbols-outlined">cloud_off</span><h2>No pudimos cargar el inventario</h2><p>{error}</p><button type="button" className="inventory-primary-button" onClick={refresh}>Reintentar</button></div> : null}
        {!loading && !error && selectedLocations.length === 0 ? <div className="inventory-empty-state"><span className="material-symbols-outlined">location_off</span><h2>Selecciona una ubicación</h2><p>El inventario no muestra productos hasta que elijas al menos una ubicación.</p></div> : null}
        {!loading && !error && selectedLocations.length > 0 && products.length === 0 ? <div className="inventory-empty-state"><span className="material-symbols-outlined">search_off</span><h2>No encontramos productos</h2><p>Prueba otra búsqueda o agrega un producto a esta ubicación.</p><button type="button" className="inventory-primary-button" onClick={openCreate}>Agregar producto</button></div> : null}
        {!loading && !error && products.map((product) => <ProductItem key={product.id} product={product} availableLocations={availableLocations} onEdit={openEditor} onAdjust={(item) => openEditor(item, "inventory")} onArchive={setArchiveTarget} />)}
      </section>

      {pagination.totalPages > 1 ? <nav className="inventory-pagination" aria-label="Paginación"><button type="button" className="inventory-secondary-button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1}><span className="material-symbols-outlined">chevron_left</span> Anterior</button><span>Página <strong>{page}</strong> de <strong>{pagination.totalPages}</strong></span><button type="button" className="inventory-secondary-button" onClick={() => setPage((value) => Math.min(pagination.totalPages, value + 1))} disabled={page >= pagination.totalPages}>Siguiente <span className="material-symbols-outlined">chevron_right</span></button></nav> : null}

      {editor ? <ProductEditorModal mode={editor.mode} product={editor.product} initialTab={editor.initialTab} availableLocations={availableLocations} categoryStructure={categoryStructure} saleUnits={saleUnits} onClose={requestEditorClose} onSaved={handleSaved} /> : null}
      {importFile ? <InventoryImportModal file={importFile} onClose={() => setImportFile(null)} onCommitted={refresh} /> : null}
      <ConfirmDialog open={Boolean(archiveTarget)} title="Archivar producto" message={`¿Archivar “${archiveTarget?.description || archiveTarget?.code || "este producto"}”? Dejará de aparecer en inventario, pero se conservarán sus ventas e historial.`} confirmLabel="Archivar" onConfirm={confirmArchive} onCancel={() => setArchiveTarget(null)} />
      <ConfirmDialog open={pendingClose} title="Descartar cambios" message="Hay cambios sin guardar. ¿Quieres cerrar el editor y descartarlos?" confirmLabel="Descartar" onConfirm={() => { setPendingClose(false); setEditor(null); }} onCancel={() => setPendingClose(false)} />
    </main>
  );
}

export default Inventory;
